/**
 * The descriptor type every tool in the catalogue is written in.
 *
 * One descriptor type, two registration paths, one source. `useToolSpec` (the
 * hook path) and `registerToolRaw` (the raw path, for `exposedTo` and for
 * `AbortSignal` driven unregistration) both read these objects in task 3.7, and
 * `validateCatalogue` reads the same objects in task 3.5. Nothing here executes
 * and nothing here reaches a host: a descriptor is data, so the build can check
 * the whole surface without a browser.
 *
 * This package holds the same line the engine holds. No React, no DOM, no app
 * imports. `packages/tools/tsconfig.json` omits `dom` from `lib` and sets
 * `types` to empty, which is why the cancellation type below is structural
 * rather than the platform `AbortSignal`.
 *
 * Covers requirements 11.1, 11.2, 11.4, 11.5 and 11.7.
 */

/** One parameter of a tool's input schema. Every tool takes a flat object. */
export interface JsonSchemaProperty {
  /** JSON Schema scalar type. `integer` where a fraction would be meaningless. */
  readonly type: 'string' | 'number' | 'integer' | 'boolean';
  /**
   * What the parameter means, naming its unit. At most 150 characters, req 11.2.
   *
   * A number without a unit is the most common source of a wrong write, so
   * "in inches" and "over four inches of swatch" are part of the contract rather
   * than politeness.
   */
  readonly description: string;
  /** Closed value set, where the parameter accepts only named values. */
  readonly enum?: ReadonlyArray<string>;
}

/**
 * The input schema of a tool whose parsed input is `I`.
 *
 * `properties` is keyed by the string keys of `I`, so a schema that describes a
 * parameter the input type does not have, or misspells one it does, is a compile
 * error rather than a runtime surprise the agent discovers. `required` is a plain
 * string list, and the check that every required name is a declared property is a
 * catalogue test, since JSON Schema allows the two to be written independently.
 */
export interface JsonSchema<I = unknown> {
  readonly type: 'object';
  readonly required?: ReadonlyArray<string>;
  readonly properties: Readonly<Record<Extract<keyof I, string>, JsonSchemaProperty>>;
}

/** The annotations a host reads off a registered tool. */
export interface ToolAnnotations {
  /**
   * True where the tool reads state and leaves the Pattern_Document alone.
   *
   * Always the negation of {@link ToolSpec.writes}, computed by `defineTool`
   * rather than typed twice, which is what makes requirement 11.4 mechanical.
   */
  readonly readOnlyHint: boolean;
  /**
   * True where the tool's output can carry content this origin did not author.
   *
   * Always equal to {@link ToolSpec.carriesExternalContent}, req 11.5.
   */
  readonly untrustedContentHint: boolean;
}

/** How a tool reaches the host. */
export type RegistrationPath = 'hook' | 'raw';

/**
 * One tool, as plain data.
 *
 * `writes` and `carriesExternalContent` are declarations of behaviour, and the
 * two annotations are derived from them. That is the whole point: requirements
 * 11.4 and 11.5 become arithmetic the build can check (task 3.5) instead of two
 * booleans a reviewer has to agree with.
 */
export interface ToolSpec<I = unknown> {
  /** At most 30 characters, req 11.1. Unique across the catalogue. */
  readonly name: string;
  /** At most 500 characters, req 11.2. What the tool does and when to use it. */
  readonly description: string;
  readonly inputSchema: JsonSchema<I>;
  readonly annotations: ToolAnnotations;
  /** True where invoking the tool can change the Pattern_Document. Req 11.4. */
  readonly writes: boolean;
  /**
   * True where output can include a string sourced outside the Jig origin, such
   * as a yarn name, a fibre description or a community note. Declared here so
   * requirement 11.5 is checkable from the descriptor alone. Req 11.5.
   */
  readonly carriesExternalContent: boolean;
  /**
   * `hook` for `useToolSpec`, `raw` for `registerToolRaw`.
   *
   * Raw is required wherever {@link exposedTo} is present, since the hook cannot
   * carry that option, and wherever unregistration is driven by aborting the
   * signal supplied at registration. Req 15.2, 26.4.
   */
  readonly registration: RegistrationPath;
  /**
   * The origins allowed to see this tool. Raw path only. Req 21.4, 26.4.
   *
   * Present means cross origin exposure, which the hook cannot express, so a
   * spec carrying this field carries `registration: 'raw'` as well.
   */
  readonly exposedTo?: ReadonlyArray<string>;
}

/**
 * A spec whose input type has been erased, for the mixed catalogue array.
 *
 * `ToolSpec<unknown>` has no schema properties of its own, so every concretely
 * typed spec is assignable to it while the concrete type stays available at each
 * call site through the named exports.
 */
export type AnyToolSpec = ToolSpec<unknown>;

/** An MCP style content array of text parts. Req 11.7. */
export interface ToolText {
  content: Array<{ type: 'text'; text: string }>;
  isError?: boolean;
}

/**
 * The slice of the platform `AbortSignal` a verb reads.
 *
 * Structural rather than the DOM type, because this package is DOM free and a
 * verb needs exactly one thing from a signal: whether the caller has given up.
 * A real `AbortSignal` satisfies it, so the wrapper in task 3.7 can pass the one
 * the host hands to `execute` straight through.
 */
export interface AbortSignalLike {
  readonly aborted: boolean;
}

/**
 * What a tool does when invoked. Returns a plain string or a content array, per
 * requirement 11.7, and never throws: the wrapper converts a thrown value into
 * an `isError` result so nothing throws across a tool boundary.
 */
export type ToolExecute<I> = (
  input: I,
  ctx: { readonly signal?: AbortSignalLike | undefined },
) => Promise<string | ToolText>;

/**
 * What `defineTool` takes: behaviour declared once, annotations derived.
 *
 * There is no way to hand this function a `readOnlyHint`, which is deliberate.
 * The only way to make a tool read only is to declare that it does not write.
 */
export interface ToolSpecInput<I> {
  readonly name: string;
  readonly description: string;
  readonly inputSchema: JsonSchema<I>;
  readonly writes: boolean;
  readonly carriesExternalContent: boolean;
  readonly registration: RegistrationPath;
  readonly exposedTo?: ReadonlyArray<string>;
}

/**
 * Build a spec, deriving both annotations from the declared behaviour.
 *
 * `readOnlyHint` is `!writes` and `untrustedContentHint` is
 * `carriesExternalContent`, here and nowhere else. Task 3.5's validator still
 * checks both on the built objects, since a spec assembled by hand somewhere
 * else would bypass this function.
 */
export const defineTool = <I>(input: ToolSpecInput<I>): ToolSpec<I> => {
  const spec: ToolSpec<I> = {
    name: input.name,
    description: input.description,
    inputSchema: input.inputSchema,
    annotations: {
      readOnlyHint: !input.writes,
      untrustedContentHint: input.carriesExternalContent,
    },
    writes: input.writes,
    carriesExternalContent: input.carriesExternalContent,
    registration: input.registration,
    ...(input.exposedTo === undefined ? {} : { exposedTo: input.exposedTo }),
  };

  return Object.freeze(spec);
};
